import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { canAccess } from '../config/roles.js';

/**
 * Role-aware route guard (deny by default).
 *
 * Derives the page slug from the first path segment and checks it against the
 * PAGE_ROLES matrix (config/roles.js):
 *   - anonymous users → redirected to /login (preserving intended destination);
 *   - authenticated users without an allowed role → blocked with the 403 page
 *     (not merely hidden);
 *   - superusers ('admin') and users whose business role is permitted → allowed.
 *
 * Wrap any set of gated routes:
 *   <Route element={<RbacRoute />}>
 *     <Route path="finance-disbursements-tax-panel" element={<FinancePage />} />
 *   </Route>
 */
export default function RbacRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const slug = location.pathname.replace(/^\/+/, '').split('/')[0];

  if (!canAccess(user, slug)) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
